import React, { forwardRef, ReactNode, useCallback } from 'react';
import styled, { css } from 'styled-components';
import colors, { TColorKeys } from '../../styles/colors';
import { newTextStyles } from './HBText.styles';
import {
    THtmlTextTag,
    TTextAlignment,
    TWeight,
    TTransform
} from './HBText.types';
import { TCustomFont } from '../../styles/fonts';
import { lineClampedText, truncatedText } from '../../styles/mixins';
import type { CSSDimensionValue } from '../../tokens/types';
import HBTooltip, { ITooltipProps } from '../HBTooltip/HBTooltip';
import { useHintedOverflowText } from '../common/internal/useHintedOverflowText';
import { useHBUI } from '../HBUIProvider/HBUIProvider';
import { vars } from '../../theme';
import { semanticColors } from '../../styles/semanticColors';

export type TTextType = keyof typeof newTextStyles;

export type TSemanticTextColorKeys = `text.${keyof typeof semanticColors.text}`;
export type TAllColorKeys = TColorKeys | TSemanticTextColorKeys | 'inherit';

export interface ITextProps {
    /** Horizontal alignment. Setting align also sets `display: inline-block` on the element. */
    align?: TTextAlignment;
    /** Text content. String children enable the overflow tooltip when truncated by `ellipsis` or `lineClamp`; JSX node children are clipped silently. */
    children?: ReactNode;
    /** Additional CSS class name(s) applied to the root element. */
    className?: string;
    /** Adds pointer cursor, button role, and keyboard support; auto-enabled when onClick is provided. */
    clickable?: boolean;
    /** Accepts legacy color keys, semantic `text.*` tokens, or `'inherit'`. */
    color?: TAllColorKeys;
    /** Truncates text with ellipsis; when combined with hinted ellipsis (via HBUIProvider), shows a tooltip on overflow. */
    ellipsis?: boolean;
    /** Overrides the font family. Avoid using this — the design system enforces a fixed type system. */
    fontFamily?: TCustomFont;
    /** Renders the text in italic style. */
    italic?: boolean;
    /** Clamps text to N lines with ellipsis; values > 1 use CSS line-clamp, value of 1 uses single-line truncation. */
    lineClamp?: number;
    /** Renders a line-through (strikethrough) decoration. Useful for showing discounted or removed values. */
    lineThrough?: boolean;
    /** Applies a margin shorthand. Also sets `display: inline-block` on the element. */
    margin?: CSSDimensionValue;
    /** Click handler; automatically enables clickable mode (button role, tabIndex, Enter key support). */
    onClick?: (event?) => void;
    /** HTML element to render as; defaults to 'span'. */
    tag?: THtmlTextTag;
    /** Sets `data-testid` on the root element. */
    testId?: string;
    /** Configures the overflow tooltip; disabled by default unless HBUIProvider sets isHintedEllipsis. */
    tooltipProps?: Omit<ITooltipProps, 'target' | 'children'> & {
        disabled: boolean;
    };
    /** Sets `data-tour-id` for product tour targeting. */
    tourId?: string;
    /** Text transform; 'capitalizeFirstWord' only capitalizes the first letter via CSS ::first-letter. */
    transform?: TTransform;
    /** Determines the typographic style (font-size, line-height, weight) from the design system type scale. */
    type: TTextType;
    /** Overrides the font weight from the type style. Use sparingly — type styles have intentional default weights. */
    weight?: TWeight;
    /** Sets the HTML `id` attribute on the root element. */
    id?: string;
}

type StyleProps = {
    type: ITextProps['type'];
    $clickable: ITextProps['clickable'];
    $align: ITextProps['align'];
    $weight: ITextProps['weight'];
    $transform: ITextProps['transform'];
    $color: ITextProps['color'];
    $ellipsis: ITextProps['ellipsis'];
    $italic: ITextProps['italic'];
    $lineClamp: ITextProps['lineClamp'];
    $fontFamily: ITextProps['fontFamily'];
    $lineThrough: ITextProps['lineThrough'];
    $margin: ITextProps['margin'];
};

const semanticTextColorEntries = Object.keys(vars.colors.text).map(key => [
    `text.${key}`,
    vars.colors.text[key as keyof typeof vars.colors.text]
]);

const allColors: Record<string, string> = {
    ...colors,
    ...Object.fromEntries(semanticTextColorEntries)
};

const resolveColor = (color: TAllColorKeys | undefined): string | undefined => {
    if (!color) {
        return undefined;
    }
    if (color === 'inherit') {
        return 'inherit';
    }
    return allColors[color];
};

const Text = styled.span<StyleProps>`
    ${props => newTextStyles[props.type]}
    ${({
        $color,
        $fontFamily,
        $align,
        $weight,
        $italic,
        $lineThrough,
        $margin,
        $ellipsis,
        $transform,
        $clickable
    }) => css`
        ${$color &&
        css`
            color: ${resolveColor($color)};
        `}
        ${$fontFamily &&
        css`
            font-family: ${$fontFamily};
        `}
        ${$align &&
        css`
            text-align: ${$align};
            display: inline-block;
        `}
        ${$weight &&
        css`
            font-weight: ${$weight};
        `}
        ${$italic &&
        css`
            font-style: italic;
        `}
        ${$lineThrough &&
        css`
            text-decoration: line-through;
        `}
        ${$margin &&
        css`
            margin: ${$margin};
            display: inline-block;
        `}
        ${$ellipsis &&
        css`
            display: block;
        `}
        ${$transform &&
        ($transform === 'capitalizeFirstWord'
            ? css`
                  display: inline-block;
                  &::first-letter {
                      text-transform: capitalize;
                  }
              `
            : css`
                  text-transform: ${$transform};
              `)}
        ${$clickable &&
        css`
            cursor: pointer;
            &:focus-visible {
                text-decoration: underline;
            }
        `}
    `}

    ${({ $ellipsis, $lineClamp }) =>
        $lineClamp > 1
            ? lineClampedText($lineClamp)
            : $ellipsis || $lineClamp === 1
            ? truncatedText
            : null}
`;

const HBText = forwardRef<HTMLSpanElement, ITextProps>(
    (
        {
            align,
            children,
            className,
            clickable: _clickable,
            color,
            ellipsis = false,
            fontFamily,
            italic,
            lineClamp,
            lineThrough,
            margin,
            onClick,
            tag = 'span',
            testId,
            tooltipProps: _tooltipProps,
            tourId,
            transform,
            type,
            weight,
            id
        },
        ref
    ) => {
        const { isHintedEllipsis: defaultIsHintedEllipsis } = useHBUI();
        const tooltipProps =
            _tooltipProps ||
            (defaultIsHintedEllipsis
                ? { disabled: false }
                : { disabled: true });
        const isHintedEllipsis =
            (ellipsis || !!lineClamp) && !tooltipProps.disabled;
        const { combineRefs, isOverflowing } = useHintedOverflowText({
            isHintedEllipsis,
            ref
        });

        const clickable = _clickable || !!onClick;

        const handleKeyUp = useCallback(
            ev => {
                if (clickable && ev.key === 'Enter' && onClick) {
                    onClick();
                }
            },
            [clickable, onClick]
        );

        const target = (
            <Text
                as={tag}
                ref={combineRefs}
                className={className}
                data-text={typeof children === 'string' ? children : undefined}
                data-testid={testId}
                data-tour-id={tourId}
                onClick={onClick}
                onKeyUp={handleKeyUp}
                role={clickable ? 'button' : null}
                tabIndex={clickable ? 0 : null}
                $align={align}
                $clickable={clickable}
                $color={color}
                $ellipsis={ellipsis}
                $italic={italic}
                $lineClamp={lineClamp}
                $transform={transform}
                type={type}
                $weight={weight}
                $fontFamily={fontFamily}
                $lineThrough={lineThrough}
                $margin={margin}
                id={id}
            >
                {children}
            </Text>
        );

        if (!isHintedEllipsis || typeof children !== 'string') {
            return target;
        }

        return (
            <HBTooltip
                forceClose={!isOverflowing}
                placement="top"
                target={target}
                targetContainerStyle={{ overflow: 'hidden' }}
                {...tooltipProps}
            >
                {children}
            </HBTooltip>
        );
    }
);

export default HBText;
